import {
  convertTaxCertificationDelinquenciesDataToTaxCertificationDelinquenciesResponse,
  convertTaxCertificationDelinquenciesResponseToTaxCertificationDelinquenciesData,
  ITaxCertificationDelinquenciesData,
  ITaxCertificationDelinquenciesResponse,
} from './ITaxCertificationDelinquencies';
import {
  convertJurisdictionDataToJurisdictionResponse,
  convertJurisdictionResponseToJurisdictionData,
  createDefaultJurisdictionData,
  IJurisdictionData,
  IJurisdictionResponse,
} from './IJurisdiction';
import { eTaxCertificationClassCodes } from './eTaxCertificationClassCodes';
import { eTaxCertificationStatus } from './eTaxCertificationStatus';
import { ICountyData } from '../orders/searchOrder/types/ICounty';
import { isNil } from 'lodash';

export interface ITaxCertificationListResponse {
  taxCertifications: ITaxCertificationResponse[];
}

export interface ITaxCertificationListData {
  taxCertifications: ITaxCertificationData[];
}

export const createDefaultTaxCertificationList = (): ITaxCertificationListData => ({
  taxCertifications: [],
});

export const defaultTaxCertificationData: ITaxCertificationData = {
  jurisdictions: [createDefaultJurisdictionData()],
  taxCertificationDelinquencies: [],
};

export interface ITaxCertificationResponse {
  id?: string;
  orderId?: string;
  parcelNumber?: number;
  classCode?: eTaxCertificationClassCodes;
  propertyOwner?: string;
  propertyStreetAddress?: string;
  propertyFloorSuite?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyCounty?: ICountyData;
  propertyZipCode?: string;
  landValueCents?: number;
  improvementValueCents?: number;
  exemptionCents?: number;
  taxableValueCents?: number;
  marketValueCents?: number;
  assessedYear?: number;
  totalAssessedValue?: String;
  assessedMarketValue?: String;
  hasPriorDelinquencies?: boolean;
  hasExemptions?: boolean;
  hasAllParcels?: boolean;
  hasOtherTaxingAuthorities?: boolean;
  exemptionsNotes?: string;
  allParcelsNotes?: string;
  otherTaxingAuthoritiesNotes?: string;
  notes?: string;
  discounts?: string;
  taxCertificationDelinquencies?: ITaxCertificationDelinquenciesResponse[];
  jurisdictions?: IJurisdictionResponse[];
  status?: eTaxCertificationStatus;
  updatedAt?: string;
}

export interface ITaxCertificationData {
  id?: string;
  orderId?: string;
  parcelNumber?: number;
  classCode?: eTaxCertificationClassCodes;

  propertyOwner?: string;
  propertyStreetAddress?: string;
  propertyFloorSuite?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyCounty?: ICountyData;
  propertyZipCode?: string;

  landValueCents?: number;
  improvementValueCents?: number;
  exemptionCents?: number;
  taxableValueCents?: number;
  marketValueCents?: number;
  assessedYear?: number;
  totalAssessedValue?: String;
  assessedMarketValue?: String;
  hasPriorDelinquencies?: boolean;
  hasExemptions?: boolean;
  hasAllParcels?: boolean;
  hasOtherTaxingAuthorities?: boolean;
  exemptionsNotes?: string;
  allParcelsNotes?: string;
  otherTaxingAuthoritiesNotes?: string;
  notes?: string;
  discounts?: string;
  taxCertificationDelinquencies: ITaxCertificationDelinquenciesData[];
  jurisdictions: IJurisdictionData[];
  status?: eTaxCertificationStatus;
  updatedAt?: string;
}

export const convertTaxCertificationResponseToTaxCertificationData = (
  res: ITaxCertificationResponse
): ITaxCertificationData => ({
  id: res.id,
  parcelNumber: res.parcelNumber,
  classCode: res.classCode,

  propertyOwner: res.propertyOwner,
  propertyStreetAddress: res.propertyStreetAddress,
  propertyFloorSuite: res.propertyFloorSuite,
  propertyCity: res.propertyCity,
  propertyState: res.propertyState,
  propertyCounty: res.propertyCounty,
  propertyZipCode: res.propertyZipCode,

  landValueCents: res.landValueCents,
  improvementValueCents: res.improvementValueCents,
  exemptionCents: res.exemptionCents,
  taxableValueCents: res.taxableValueCents,
  marketValueCents: res.marketValueCents,
  assessedYear: res.assessedYear,
  totalAssessedValue: res.totalAssessedValue,
  assessedMarketValue: res.assessedMarketValue,
  hasPriorDelinquencies: res.hasPriorDelinquencies,
  hasExemptions: res.hasExemptions,
  exemptionsNotes: res.exemptionsNotes,
  hasAllParcels: res.hasAllParcels,
  allParcelsNotes: res.allParcelsNotes,
  hasOtherTaxingAuthorities: res.hasOtherTaxingAuthorities,
  otherTaxingAuthoritiesNotes: res.otherTaxingAuthoritiesNotes,
  notes: res.notes,
  discounts: res.discounts,
  jurisdictions: isNil(res.jurisdictions)
    ? []
    : res.jurisdictions!.map((j) => convertJurisdictionResponseToJurisdictionData(j)),
  taxCertificationDelinquencies: isNil(res.taxCertificationDelinquencies)
    ? []
    : res.taxCertificationDelinquencies!.map((tcd) =>
        convertTaxCertificationDelinquenciesResponseToTaxCertificationDelinquenciesData(tcd)
      ),
  status: res.status,
  updatedAt: res.updatedAt,
});

export const convertTaxCertificationDataToTaxCertificationResponse = (
  data: ITaxCertificationData
): ITaxCertificationResponse => ({
  parcelNumber: data.parcelNumber,
  classCode: data.classCode,

  propertyOwner: data.propertyOwner,
  propertyStreetAddress: data.propertyStreetAddress,
  propertyFloorSuite: data.propertyFloorSuite,
  propertyCity: data.propertyCity,
  propertyState: data.propertyState,
  propertyCounty: data.propertyCounty,
  propertyZipCode: data.propertyZipCode,

  landValueCents: data.landValueCents,
  improvementValueCents: data.improvementValueCents,
  exemptionCents: data.exemptionCents,
  taxableValueCents: data.taxableValueCents,
  marketValueCents: data.marketValueCents,
  assessedYear: data.assessedYear,
  totalAssessedValue: data.totalAssessedValue,
  assessedMarketValue: data.assessedMarketValue,
  hasPriorDelinquencies: data.hasPriorDelinquencies,
  hasExemptions: data.hasExemptions,
  hasAllParcels: data.hasAllParcels,
  hasOtherTaxingAuthorities: data.hasOtherTaxingAuthorities,
  allParcelsNotes: data.allParcelsNotes,
  otherTaxingAuthoritiesNotes: data.otherTaxingAuthoritiesNotes,
  notes: data.notes,
  discounts: data.discounts,
  jurisdictions: isNil(data.jurisdictions)
    ? []
    : data.jurisdictions!.map((j) => convertJurisdictionDataToJurisdictionResponse(j)),
  taxCertificationDelinquencies: isNil(data.taxCertificationDelinquencies)
    ? data.taxCertificationDelinquencies
    : data.taxCertificationDelinquencies!.map((tcd) =>
        convertTaxCertificationDelinquenciesDataToTaxCertificationDelinquenciesResponse(tcd)
      ),
});

export const convertTaxCertificationListDataToTaxCertificationListResponse = (
  data: ITaxCertificationListData
): ITaxCertificationListResponse => ({
  taxCertifications: isNil(data.taxCertifications)
    ? []
    : data.taxCertifications!.map((tc) => convertTaxCertificationDataToTaxCertificationResponse(tc)),
});

export const convertTaxCertificationListResponseToTaxCertificationListData = (
  res: ITaxCertificationListResponse
): ITaxCertificationListData => ({
  taxCertifications: isNil(res.taxCertifications)
    ? []
    : res.taxCertifications!.map((tc) => convertTaxCertificationResponseToTaxCertificationData(tc)),
});
